import { Controller } from "@hotwired/stimulus"
import { reactor } from "../lib/reactor"

// Fills free space under a short list with complete skeleton rows only.
// Never pads past the visible height (no phantom scrollbar, no half-cut ghosts).
// Count is driven only by container height (ResizeObserver), not a fixed cap.
//
// Markup: controller on the scroll list, a <template> for one row, optional
// data-activity-skeleton-row on cloned roots so refills can clear them.
export default class extends Controller {
  static targets = ["template"]

  connect() {
    reactor(this)
    this.boundFill = this.fill.bind(this)
    this.resizeObserver = new ResizeObserver(this.boundFill)
    this.resizeObserver.observe(this.element)
    this.fill()
  }

  disconnect() {
    this.resizeObserver?.disconnect()
  }

  morph() {
    this.fill()
  }

  fill() {
    if (!this.hasTemplateTarget || this.filling) return
    if (this.element.clientHeight === 0) return

    this.filling = true
    try {
      this.clear()

      while (true) {
        const row = this.appendRow()
        // Zero-height clone would never overflow: abort rather than spin.
        if (row.offsetHeight === 0 || this.element.scrollHeight > this.element.clientHeight) {
          row.remove()
          break
        }
      }

      this.notifyScrollFade()
    } finally {
      this.filling = false
    }
  }

  clear() {
    this.element
      .querySelectorAll("[data-activity-skeleton-row]")
      .forEach((el) => el.remove())
  }

  appendRow() {
    const root = this.templateTarget.content.firstElementChild
    if (!root) return document.createElement("div")

    const row = root.cloneNode(true)
    row.setAttribute("data-activity-skeleton-row", "")
    row.setAttribute("aria-hidden", "true")
    // Keep ghosts before the inert <template> so divide-y only spans real+ghost rows.
    this.element.insertBefore(row, this.templateTarget)
    return row
  }

  notifyScrollFade() {
    const host = this.element.closest("[data-controller~='scroll-fade']")
    if (!host) return

    const scrollFade = this.application.getControllerForElementAndIdentifier(
      host,
      "scroll-fade"
    )
    scrollFade?.check()
  }
}
